import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Clock,
  DollarSign,
  LogOut,
  Mail,
  Phone,
  Repeat,
  Trash2,
  UserPlus,
  XCircle,
} from "lucide-react";
import Logo from "@/components/Logo";
import AddClientModal from "@/components/AddClientModal";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const STATUSES = ["all", "pending", "accepted", "completed", "rejected", "recurring"];
const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  accepted: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};
const PIE_COLORS = ["#0a0a0a", "#00E676", "#666666", "#cccccc"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [filter, setFilter] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      let bookingsUrl = "/bookings";
      if (filter === "recurring") bookingsUrl = "/bookings?recurring=true";
      else if (filter !== "all") bookingsUrl = `/bookings?status=${filter}`;
      const [bRes, sRes] = await Promise.all([
        api.get(bookingsUrl),
        api.get("/stats"),
      ]);
      setBookings(bRes.data);
      setStats(sRes.data);
    } catch (_) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const update = async (id, body) => {
    try {
      await api.patch(`/bookings/${id}`, body);
      toast.success("Updated");
      await load();
      if (selected && selected.id === id) {
        const refreshed = await api.get(`/bookings`);
        const found = refreshed.data.find((x) => x.id === id);
        setSelected(found || null);
      }
    } catch (_) {
      toast.error("Update failed");
    }
  };

  const remove = async (id, opts = {}) => {
    if (!opts.silent && !window.confirm("Delete this booking? This can't be undone.")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  const serviceChartData = useMemo(() => {
    if (!stats?.by_service) return [];
    return Object.entries(stats.by_service).map(([k, v]) => ({ name: k, value: v }));
  }, [stats]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[var(--pc-grey)] text-black">
      {/* Topbar */}
      <header className="sticky top-0 z-30 border-b border-black/10 bg-white">
        <div className="pc-container flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display text-lg font-extrabold">
            <Logo variant="light" size={36} />
            Pristyn<span className="text-[var(--pc-green-hover)]">.</span>
            <span className="ml-2 text-xs uppercase tracking-[0.25em] text-black/40">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden text-sm text-black/60 md:block">
              {user?.email}
            </div>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-black/15 px-4 py-2 text-sm font-medium transition hover:border-black hover:bg-black hover:text-white"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="pc-container py-10">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="eyebrow text-[var(--pc-green-hover)]">Overview</div>
            <h1 className="font-display mt-2 text-4xl font-black tracking-tighter md:text-5xl">
              Business at a glance
            </h1>
          </div>
          <button
            data-testid="add-client-button"
            onClick={() => setShowAddClient(true)}
            className="pc-btn-primary text-sm"
          >
            <UserPlus size={16} /> Add Client
          </button>
        </div>

        {/* KPI Cards */}
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <KpiCard data-testid="kpi-total" icon={CalendarDays} label="Total Bookings" value={stats?.total_bookings ?? 0} />
          <KpiCard data-testid="kpi-pending" icon={Clock} label="Pending" value={stats?.pending ?? 0} />
          <KpiCard data-testid="kpi-completed" icon={CheckCircle2} label="Completed" value={stats?.completed ?? 0} accent />
          <KpiCard
            data-testid="kpi-revenue"
            icon={DollarSign}
            label="Revenue (Completed)"
            value={`$${(stats?.total_revenue ?? 0).toLocaleString()}`}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-black/10 bg-white p-6">
            <div className="eyebrow text-black/40">Estimated Pipeline</div>
            <div className="mt-2 font-display text-3xl font-black tracking-tight">
              ${(stats?.estimated_revenue ?? 0).toLocaleString()}
            </div>
            <p className="mt-1 text-sm text-black/50">Quotes accepted + pending</p>
          </div>
          <div className="rounded-2xl border border-black/10 bg-white p-6 md:col-span-2">
            <div className="eyebrow text-black/40">Monthly Revenue (Completed)</div>
            <div className="mt-3 h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.monthly_revenue ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                  <XAxis dataKey="month" stroke="#999" fontSize={12} />
                  <YAxis stroke="#999" fontSize={12} />
                  <Tooltip cursor={{ fill: "rgba(0,230,118,0.08)" }} />
                  <Bar dataKey="revenue" fill="#00E676" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-black/10 bg-white p-6 lg:col-span-1">
            <div className="eyebrow text-black/40">Bookings by Service</div>
            <div className="mt-3 h-56">
              {serviceChartData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-black/40">
                  No data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={serviceChartData} dataKey="value" outerRadius={70} innerRadius={40}>
                      {serviceChartData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Bookings table */}
          <div className="rounded-2xl border border-black/10 bg-white p-6 lg:col-span-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="eyebrow text-black/40">Bookings</div>
                <h3 className="font-display mt-1 text-xl font-bold tracking-tight">All Estimate Requests</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`filter-${s}`}
                    onClick={() => setFilter(s)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize transition ${
                      filter === s
                        ? "border-black bg-black text-white"
                        : "border-black/15 text-black/60 hover:border-black"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 overflow-x-auto">
              {loading ? (
                <div className="py-10 text-center text-sm text-black/40">Loading…</div>
              ) : bookings.length === 0 ? (
                <div className="py-12 text-center text-sm text-black/40" data-testid="bookings-empty">
                  No bookings here yet.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="bookings-table">
                  <thead className="text-left text-xs uppercase tracking-[0.18em] text-black/40">
                    <tr>
                      <th className="py-3 pr-4">Customer</th>
                      <th className="py-3 pr-4">Service</th>
                      <th className="py-3 pr-4">Date</th>
                      <th className="py-3 pr-4">Status</th>
                      <th className="py-3 pr-4">Quote</th>
                      <th className="py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr
                        key={b.id}
                        data-testid={`booking-row-${b.id}`}
                        className="border-t border-black/5 hover:bg-black/[.02]"
                      >
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <div>
                              <div className="font-semibold">{b.name}</div>
                              <div className="text-xs text-black/50">{b.email}</div>
                            </div>
                            {b.is_recurring && (
                              <span
                                data-testid={`recurring-badge-${b.id}`}
                                className="inline-flex items-center gap-1 rounded-full bg-[var(--pc-green-light)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-800"
                              >
                                <Repeat size={10} /> Recurring
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 pr-4">{b.service_type}</td>
                        <td className="py-3 pr-4">
                          {b.preferred_date}
                          {b.preferred_time ? ` · ${b.preferred_time}` : ""}
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`rounded-full border px-2 py-1 text-xs font-semibold capitalize ${STATUS_COLORS[b.status] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="py-3 pr-4">${(b.estimated_value || 0).toLocaleString()}</td>
                        <td className="py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              data-testid={`booking-view-${b.id}`}
                              onClick={() => setSelected(b)}
                              className="rounded-full border border-black/15 px-3 py-1.5 text-xs font-semibold hover:border-black"
                            >
                              View
                            </button>
                            <button
                              data-testid={`booking-delete-${b.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                remove(b.id);
                              }}
                              className="grid h-8 w-8 place-items-center rounded-full border border-red-200 text-red-600 hover:bg-red-50"
                              aria-label="Delete booking"
                              title="Delete booking"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="booking-drawer">
          <button
            aria-label="Close"
            onClick={() => setSelected(null)}
            className="flex-1 bg-black/40"
          />
          <aside className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-2xl md:p-8">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="eyebrow text-black/40">Booking</div>
                <h3 className="font-display mt-1 text-2xl font-bold tracking-tight">{selected.name}</h3>
              </div>
              <button
                data-testid="drawer-close"
                onClick={() => setSelected(null)}
                className="rounded-full border border-black/15 p-2 hover:border-black"
                aria-label="Close drawer"
              >
                <XCircle size={18} />
              </button>
            </div>

            <div className="mt-6 space-y-3 text-sm">
              <Row icon={Phone} label={selected.phone} />
              <Row icon={Mail} label={selected.email} />
              <Row icon={CalendarDays} label={`${selected.preferred_date} ${selected.preferred_time || ""}`} />
              <div className="rounded-xl border border-black/10 bg-[var(--pc-grey)] p-4">
                <div className="eyebrow text-black/40">Service</div>
                <div className="mt-1 font-semibold">{selected.service_type}</div>
                <div className="mt-3 eyebrow text-black/40">Address</div>
                <div className="mt-1">{selected.address}</div>
                {selected.message && (
                  <>
                    <div className="mt-3 eyebrow text-black/40">Customer Notes</div>
                    <div className="mt-1 whitespace-pre-wrap text-black/80">{selected.message}</div>
                  </>
                )}
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-black/60">
                  Estimated quote (CAD)
                </label>
                <input
                  data-testid="drawer-estimate-input"
                  type="number"
                  min="0"
                  step="1"
                  defaultValue={selected.estimated_value || 0}
                  onBlur={(e) =>
                    update(selected.id, { estimated_value: Number(e.target.value) })
                  }
                  className="w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm focus:border-[var(--pc-green)] focus:outline-none focus:ring-4 focus:ring-[rgba(0,230,118,0.18)]"
                />
                <p className="mt-1 text-xs text-black/40">Saves on blur.</p>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-black/60">
                  Admin notes
                </label>
                <textarea
                  data-testid="drawer-notes"
                  defaultValue={selected.admin_notes || ""}
                  onBlur={(e) => update(selected.id, { admin_notes: e.target.value })}
                  className="min-h-[90px] w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm focus:border-[var(--pc-green)] focus:outline-none focus:ring-4 focus:ring-[rgba(0,230,118,0.18)]"
                />
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2">
              <ActionBtn
                data-testid="drawer-accept"
                onClick={() => update(selected.id, { status: "accepted" })}
                color="blue"
              >
                Accept
              </ActionBtn>
              <ActionBtn
                data-testid="drawer-reject"
                onClick={() => update(selected.id, { status: "rejected" })}
                color="red"
              >
                Reject
              </ActionBtn>
              <ActionBtn
                data-testid="drawer-complete"
                onClick={() => update(selected.id, { status: "completed" })}
                color="green"
              >
                Mark Complete
              </ActionBtn>
              <ActionBtn
                data-testid="drawer-pending"
                onClick={() => update(selected.id, { status: "pending" })}
                color="gray"
              >
                Reset to Pending
              </ActionBtn>
            </div>

            <button
              data-testid="drawer-toggle-recurring"
              onClick={() => update(selected.id, { is_recurring: !selected.is_recurring })}
              className={`mt-4 flex w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                selected.is_recurring
                  ? "bg-[var(--pc-green-light)] text-emerald-800 hover:bg-emerald-100"
                  : "border border-black/15 hover:border-black"
              }`}
            >
              <Repeat size={14} />
              {selected.is_recurring ? "Recurring client" : "Mark as recurring"}
            </button>

            <button
              data-testid="drawer-delete"
              onClick={() => remove(selected.id)}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
            >
              <Trash2 size={14} /> Delete booking
            </button>
          </aside>
        </div>
      )}

      <AddClientModal
        open={showAddClient}
        onClose={() => setShowAddClient(false)}
        onCreated={load}
      />
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, ...rest }) {
  return (
    <div
      {...rest}
      className={`rounded-2xl border p-6 ${
        accent
          ? "border-[var(--pc-green)] bg-black text-white"
          : "border-black/10 bg-white"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className={`eyebrow ${accent ? "text-[var(--pc-green)]" : "text-black/40"}`}>
          {label}
        </div>
        <Icon size={18} className={accent ? "text-[var(--pc-green)]" : "text-black/40"} />
      </div>
      <div className="font-display mt-3 text-3xl font-black tracking-tight md:text-4xl">{value}</div>
    </div>
  );
}

function Row({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-9 w-9 place-items-center rounded-full border border-black/10 text-black/70">
        <Icon size={14} />
      </span>
      <span className="font-medium">{label}</span>
    </div>
  );
}

function ActionBtn({ children, color = "gray", ...props }) {
  const map = {
    blue: "bg-blue-600 hover:bg-blue-700 text-white",
    red: "bg-red-600 hover:bg-red-700 text-white",
    green: "bg-[var(--pc-green)] hover:bg-[var(--pc-green-hover)] text-black",
    gray: "border border-black/15 hover:border-black",
  };
  return (
    <button
      {...props}
      className={`rounded-full px-4 py-2.5 text-sm font-semibold transition ${map[color]}`}
    >
      {children}
    </button>
  );
}
